/**
 * iPad Dashboard Watchlist Sync Hook
 *
 * Synchronizes watchlist data between localStorage and Supabase
 * - Authenticated users: sync to database
 * - Non-authenticated users: localStorage only
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase/client';
import { useAuthStore } from '../store/authStore';
import { StorageService } from '../services/storage';

// Storage key for localStorage fallback
const WATCHLIST_STORAGE_KEY = 'dashboard_ipad_watchlist';

// Alert condition types
type AlertConditionType = 'price_above' | 'price_below' | 'change_above' | 'change_below';

interface TokenAlert {
  id: string;
  type: AlertConditionType;
  value: number;
  colorId: string;
  enabled: boolean;
}

interface WatchlistToken {
  id: string;
  symbol: string;
  name: string;
  costBasis?: number;
  image?: string;
  alerts?: TokenAlert[];
}

interface WatchlistConfig {
  tokens: WatchlistToken[];
  refreshInterval: number;
  showClock: boolean;
  gridCols: 2 | 3 | 4;
  rowsPerPage: 2 | 3 | 4;
}

const DEFAULT_CONFIG: WatchlistConfig = {
  tokens: [],
  refreshInterval: 30,
  showClock: true,
  gridCols: 3,
  rowsPerPage: 4,
};

interface UseIpadWatchlistSyncReturn {
  config: WatchlistConfig;
  setConfig: (config: WatchlistConfig | ((prev: WatchlistConfig) => WatchlistConfig)) => void;
  isLoading: boolean;
  isSyncing: boolean;
  lastSyncedAt: Date | null;
  syncError: string | null;
  forceSync: () => Promise<void>;
}

export function useIpadWatchlistSync(): UseIpadWatchlistSyncReturn {
  const { user, isAuthenticated } = useAuthStore();
  const [config, setConfigState] = useState<WatchlistConfig>(() => {
    // Initialize from localStorage
    return StorageService.get<WatchlistConfig>(WATCHLIST_STORAGE_KEY, DEFAULT_CONFIG) || DEFAULT_CONFIG;
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  // Debounce ref for saving
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingConfigRef = useRef<WatchlistConfig | null>(null);

  // Load data from Supabase on mount (if authenticated)
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      loadFromSupabase();
    } else {
      setIsLoading(false);
    }
  }, [isAuthenticated, user?.id]);

  // Load watchlist from Supabase
  const loadFromSupabase = useCallback(async () => {
    if (!user?.id) return;

    setIsLoading(true);
    setSyncError(null);

    try {
      // Load config
      const { data: configData, error: configError } = await supabase
        .from('ipad_dashboard_config')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (configError && configError.code !== 'PGRST116') {
        // PGRST116 = no rows found (normal for new users)
        throw configError;
      }

      // Load tokens with their alerts
      const { data: tokensData, error: tokensError } = await supabase
        .from('ipad_watchlist_tokens')
        .select(`
          *,
          alerts:ipad_watchlist_alerts(*)
        `)
        .eq('user_id', user.id)
        .order('display_order', { ascending: true });

      if (tokensError) throw tokensError;

      // Build config from database data
      const dbConfig: WatchlistConfig = {
        refreshInterval: configData?.refresh_interval ?? DEFAULT_CONFIG.refreshInterval,
        showClock: configData?.show_clock ?? DEFAULT_CONFIG.showClock,
        gridCols: (configData?.grid_cols as 2 | 3 | 4) ?? DEFAULT_CONFIG.gridCols,
        rowsPerPage: (configData?.rows_per_page as 2 | 3 | 4) ?? DEFAULT_CONFIG.rowsPerPage,
        tokens: (tokensData || []).map((t: any) => ({
          id: t.coin_id,
          symbol: t.symbol,
          name: t.name,
          costBasis: t.cost_basis ? parseFloat(t.cost_basis) : undefined,
          image: t.image_url || undefined,
          alerts: (t.alerts || []).map((a: any) => ({
            id: a.id,
            type: a.alert_type as AlertConditionType,
            value: parseFloat(a.target_value),
            colorId: a.color_id,
            enabled: a.is_enabled,
          })),
        })),
      };

      // Merge with localStorage (prefer DB data but keep local if newer)
      const localConfig = StorageService.get<WatchlistConfig>(WATCHLIST_STORAGE_KEY, DEFAULT_CONFIG);

      // If DB has data, use it; otherwise keep local
      if (configData || (tokensData && tokensData.length > 0)) {
        setConfigState(dbConfig);
        // Update localStorage to match DB
        StorageService.set(WATCHLIST_STORAGE_KEY, dbConfig);
      } else if (localConfig && localConfig.tokens.length > 0) {
        // No DB data but local data exists - sync local to DB
        await saveToSupabase(localConfig);
      }

      setLastSyncedAt(new Date());
    } catch (error: any) {
      console.error('Failed to load watchlist from Supabase:', error);
      setSyncError(error.message || 'Failed to load watchlist');
      // Keep using localStorage data on error
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  // Save watchlist to Supabase
  const saveToSupabase = useCallback(async (configToSave: WatchlistConfig) => {
    if (!user?.id || !isAuthenticated) return;

    setIsSyncing(true);
    setSyncError(null);

    try {
      // 1. Upsert config
      const { error: configError } = await supabase
        .from('ipad_dashboard_config')
        .upsert({
          user_id: user.id,
          grid_cols: configToSave.gridCols,
          rows_per_page: configToSave.rowsPerPage,
          show_clock: configToSave.showClock,
          refresh_interval: configToSave.refreshInterval,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id',
        });

      if (configError) throw configError;

      // 2. Get existing tokens to find which to delete
      const { data: existingTokens, error: fetchError } = await supabase
        .from('ipad_watchlist_tokens')
        .select('id, coin_id')
        .eq('user_id', user.id);

      if (fetchError) throw fetchError;

      const newCoinIds = new Set(configToSave.tokens.map(t => t.id));

      // Delete tokens that are no longer in the list
      const tokensToDelete = (existingTokens || [])
        .filter((t: any) => !newCoinIds.has(t.coin_id))
        .map((t: any) => t.id);

      if (tokensToDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from('ipad_watchlist_tokens')
          .delete()
          .in('id', tokensToDelete);

        if (deleteError) throw deleteError;
      }

      // 3. Upsert tokens
      for (let i = 0; i < configToSave.tokens.length; i++) {
        const token = configToSave.tokens[i];

        // Check if token exists
        const existingToken = (existingTokens || []).find((t: any) => t.coin_id === token.id);

        let tokenId: string;

        if (existingToken) {
          // Update existing token
          const { error: updateError } = await supabase
            .from('ipad_watchlist_tokens')
            .update({
              symbol: token.symbol,
              name: token.name,
              image_url: token.image || null,
              cost_basis: token.costBasis || null,
              display_order: i,
              updated_at: new Date().toISOString(),
            })
            .eq('id', existingToken.id);

          if (updateError) throw updateError;
          tokenId = existingToken.id;
        } else {
          // Insert new token
          const { data: insertedToken, error: insertError } = await supabase
            .from('ipad_watchlist_tokens')
            .insert({
              user_id: user.id,
              coin_id: token.id,
              symbol: token.symbol,
              name: token.name,
              image_url: token.image || null,
              cost_basis: token.costBasis || null,
              display_order: i,
            })
            .select('id')
            .single();

          if (insertError) throw insertError;
          tokenId = insertedToken.id;
        }

        // 4. Handle alerts for this token
        // Delete existing alerts
        await supabase
          .from('ipad_watchlist_alerts')
          .delete()
          .eq('token_id', tokenId);

        // Insert new alerts
        if (token.alerts && token.alerts.length > 0) {
          const alertsToInsert = token.alerts.map(alert => ({
            user_id: user.id,
            token_id: tokenId,
            alert_type: alert.type,
            target_value: alert.value,
            color_id: alert.colorId,
            is_enabled: alert.enabled,
          }));

          const { error: alertsError } = await supabase
            .from('ipad_watchlist_alerts')
            .insert(alertsToInsert);

          if (alertsError) throw alertsError;
        }
      }

      setLastSyncedAt(new Date());
    } catch (error: any) {
      console.error('Failed to save watchlist to Supabase:', error);
      setSyncError(error.message || 'Failed to save watchlist');
    } finally {
      setIsSyncing(false);
    }
  }, [user?.id, isAuthenticated]);

  // Debounced save function
  const debouncedSave = useCallback((newConfig: WatchlistConfig) => {
    // Always save to localStorage immediately
    StorageService.set(WATCHLIST_STORAGE_KEY, newConfig);

    // Debounce Supabase save
    if (!isAuthenticated || !user?.id) return;

    pendingConfigRef.current = newConfig;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      if (pendingConfigRef.current) {
        saveToSupabase(pendingConfigRef.current);
        pendingConfigRef.current = null;
      }
    }, 1000); // 1 second debounce
  }, [isAuthenticated, user?.id, saveToSupabase]);

  // Wrapper for setConfig that also saves
  const setConfig = useCallback((
    updater: WatchlistConfig | ((prev: WatchlistConfig) => WatchlistConfig)
  ) => {
    setConfigState(prev => {
      const newConfig = typeof updater === 'function' ? updater(prev) : updater;
      debouncedSave(newConfig);
      return newConfig;
    });
  }, [debouncedSave]);

  // Force sync function
  const forceSync = useCallback(async () => {
    if (isAuthenticated && user?.id) {
      await saveToSupabase(config);
    }
  }, [isAuthenticated, user?.id, config, saveToSupabase]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        // Save any pending changes before unmount
        if (pendingConfigRef.current && isAuthenticated && user?.id) {
          saveToSupabase(pendingConfigRef.current);
        }
      }
    };
  }, [isAuthenticated, user?.id, saveToSupabase]);

  return {
    config,
    setConfig,
    isLoading,
    isSyncing,
    lastSyncedAt,
    syncError,
    forceSync,
  };
}

export type { WatchlistConfig, WatchlistToken, TokenAlert, AlertConditionType };
